
import { BlastEvent, SimulationConfig } from '../types';
import { drawGravityDust } from '../break/GravityDust';
import { drawCollisionEffect as dce } from '../break/CollisionEffects';
import { BLAST_ANIMATIONS } from '../logic/animations/blasts/BlastAnimationRegistry';

// Re-export drawCollisionEffect from the new location to maintain compatibility
export const drawCollisionEffect = dce;

export const drawBlastEvent = (
  ctx: CanvasRenderingContext2D,
  e: BlastEvent,
  config: SimulationConfig,
  globalHue: number,
  canvasWidth: number,
  canvasHeight: number,
  camera: { x: number, y: number, scale: number }
) => {
  const elapsed = Date.now() - e.startTime;
  if (elapsed > e.duration) return;
  
  const progress = elapsed / e.duration;
  const timeSeconds = elapsed / 1000; // Real-time for physics
  
  const anim = e.animationType || 'EXPLOSION';
  const useRGB = config.rgbLighting && config.useFXRGB && !config.blastMatchRingColor;
  
  ctx.save();
  ctx.globalAlpha = 1 - progress;
  const blastHue1 = (globalHue + progress * 720) % 360; 
  
  // Resolve color in real-time to respect toggles even for existing events
  let resolvedColor = e.color || config.blastColor;
  if (!config.rgbLighting && resolvedColor.startsWith('hsl')) {
      resolvedColor = config.blastColor || '#ffffff';
  }
  
  ctx.strokeStyle = useRGB ? `hsl(${blastHue1}, 100%, 60%)` : resolvedColor;
  ctx.fillStyle = ctx.strokeStyle;
  
  // Optimization: Cull entirely if off-screen
  if (config.enableFrustumCulling && config.cullBlastsOutsideScreen && camera && canvasWidth && canvasHeight) {
      const halfW = canvasWidth / 2;
      const halfH = canvasHeight / 2;
      const scale = camera.scale || 1;
      const margin = (config.frustumCullingMargin || 100) + e.maxRadius + 200; // Extra margin for expansion
      
      const left = -halfW / scale - camera.x - margin;
      const right = halfW / scale - camera.x + margin;
      const top = -halfH / scale - camera.y - margin;
      const bottom = halfH / scale - camera.y + margin;

      if (e.pos.x < left || e.pos.x > right || e.pos.y < top || e.pos.y > bottom) {
          ctx.restore();
          return;
      }
  }

  // Collider Bounds
  const colliderEnabled = config.outsideColliderEnabled;
  const cCX = (config.outsideColliderOffsetX || 0);
  const cCY = (config.outsideColliderOffsetY || 0);
  const cHW = (config.outsideColliderWidth * config.outsideColliderScale) / 2;
  const cHH = (config.outsideColliderHeight * config.outsideColliderScale) / 2;
  const cL = cCX - cHW;
  const cR = cCX + cHW;
  const cT = cCY - cHH;
  const cB = cCY + cHH;

  const clampX = (x: number) => colliderEnabled ? Math.max(cL, Math.min(cR, x)) : x;
  const clampY = (y: number) => colliderEnabled ? Math.max(cT, Math.min(cB, y)) : y;

  if (anim === 'GRAVITY_DUST') {
      drawGravityDust(ctx, e, config, progress, timeSeconds, clampX, clampY, camera, canvasWidth, canvasHeight);
      ctx.restore();
      return;
  }

  const animationFn = BLAST_ANIMATIONS[anim];
  if (animationFn) {
    animationFn(ctx, e, config, progress, timeSeconds, clampX, clampY, globalHue, camera, canvasWidth, canvasHeight);
  }

  ctx.restore();
};
